/* eslint-disable no-loop-func */
import React from 'react';
import styled from 'styled-components';
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronDoubleLeft,
  mdiChevronDoubleRight,
} from '@mdi/js';
import { Icon } from '@mdi/react';
import {
  useTable,
  useBlockLayout,
  useResizeColumns,
  useExpanded,
  usePagination,
  Column,
} from 'react-table';

import { BusyIndicator, IconButton } from '@mollybet/ui';
import { api } from 'api';

export const MiddleAlignedCell = styled.div`
  text-align: center;
`;

export const RightAlignedCell = styled.div`
  text-align: right;
`;

export const DefaultTableStyle = styled.div`
  width: max-content;

  .table {
    border-radius: 5px;
    box-shadow: ${(props) => props.theme.shadows[1]};
  }

  .th {
    border-bottom: 1px solid ${(props) => props.theme.disabled};
    font-weight: 400;
  }

  .td {
    border-bottom: 2px solid ${(props) => props.theme.background};
    font-size: 85%;
  }

  .td,
  .th {
    padding: 0.5em;
    word-wrap: break-word;

    &:nth-child(2n) {
      background-color: ${(props) => props.theme.cardLevel2};
    }
    &:nth-child(2n + 1) {
      background-color: ${(props) => props.theme.cardLevel1};
    }
  }

  .thead .tr:first-child {
    .th {
      &:first-child {
        border-top-left-radius: 5px;
      }
      &:last-child {
        border-top-right-radius: 5px;
      }
    }
  }

  .tbody .tr:last-child {
    .td {
      border-bottom: none;
      &:first-child {
        border-bottom-left-radius: 5px;
      }
      &:last-child {
        border-bottom-right-radius: 5px;
      }
    }
  }

  .noData {
    padding: 10px 0px;
    text-align: center;
  }

  .button {
    padding: 0px;
    margin: 0px;
  }

  .resizer {
    display: inline-block;
    width: 10px;
    height: 100%;
    position: absolute;
    right: 0;
    top: 0;
    transform: translateX(50%);
    z-index: 1;
    ${'' /* prevents from scrolling while dragging on touch devices */}
    touch-action:none;
  }

  .tableFooter {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${(props) => props.theme.cardLevel2};
    border-radius: 0px 0px 5px 5px;
  }

  .pageCount {
    border: solid 1px ${(props) => props.theme.foreground};
    border-radius: 5px;
    font-weight: 450;
    font-size: 10pt;
    line-height: 25px;
    text-align: center;
    height: 25px;
    width: 25px;
  }

  .pagination {
    display: flex;
    padding: 12px 10px 12px 0px;
    justify-content: flex-end;
    align-items: center;
  }

  .loadingIcon {
    margin-left: 10px;
    margin-top: 5px;
  }

  .loadingMask {
    background-color: #000000;
    opacity: 0.2;
  }

  .subTable {
    padding: 5px 0px 5px 10px;
  }
`;

export const DefaultSubTableStyle = styled.div`
  width: max-content;

  .table {
    box-shadow: ${(props) => props.theme.shadows[0]};
  }

  .td,
  .th {
    &:nth-child(2n) {
      background-color: ${(props) => props.theme.cardLevel1};
    }
    &:nth-child(2n + 1) {
      background-color: ${(props) => props.theme.cardLevel2};
    }
  }

  .tbody .tr:last-child {
    border-bottom: 3px solid ${(props) => props.theme.background};
    .td {
      &:first-child {
        border-bottom-left-radius: 5px;
      }
      &:last-child {
        border-bottom-right-radius: 5px;
      }
    }
  }
`;

export const TableHeader = styled.div`
  display: flex;
  align-items: center;
  margin-top: 5px;

  & > * {
    margin: 0px 10px;

    &:first-child {
      margin-left: 0;
    }
  }
`;

const TitleContainer = styled.div`
  display: inline-flex;
  align-items: center;
  margin: 2rem 0 1rem;
`;

const StyledIcon = styled(Icon).attrs((props) => ({
  size: 1,
}))`
  color: ${(props) => props.theme.foreground};
`;

const Title = styled.div`
  margin-left: 5px;
`;

export function TableTitle({ icon, title }) {
  return (
    <TitleContainer>
      <StyledIcon path={icon} />
      <Title>{title}</Title>
    </TitleContainer>
  );
}

/**
 * A resizeable table using react-table
 * @param columns The memoized headers of the table in the correct shape
 * @param data The memoized data of shape [{accessor1:"value1"...accessorN:"valueN"}]
 */
export function UnstyledResizeableTable({ columns, data, titleNode, loading }) {
  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 30,
      width: 115,
      maxWidth: 400,
    }),
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        defaultColumn,
      },
      useBlockLayout,
      useResizeColumns
    );

  return (
    <>
      {titleNode}
      <div {...getTableProps()} className="table">
        <div className="thead">
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} className="tr">
              {headerGroup.headers.map((column) => (
                <div {...column.getHeaderProps()} className="th">
                  {column.render('Header')}
                  {/* Use column.getResizerProps to hook up the events correctly */}
                  <div
                    {...column.getResizerProps()}
                    className={`resizer ${
                      column.isResizing ? 'isResizing' : ''
                    }`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>

        <div {...getTableBodyProps()} className="tbody">
          {loading ? (
            <div className="tr">
              <div className="td noData">
                <BusyIndicator width={24} />
              </div>
            </div>
          ) : !data.length ? (
            <div className="tr">
              <div className="td noData">None available</div>
            </div>
          ) : (
            rows.map((row, i) => {
              prepareRow(row);
              return (
                <div {...row.getRowProps()} className="tr">
                  {row.cells.map((cell) => {
                    return (
                      <div {...cell.getCellProps()} className="td">
                        {cell.render('Cell')}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

export function StaticTable({ columns, data, titleNode }) {
  return (
    <DefaultTableStyle>
      <UnstyledResizeableTable
        columns={columns}
        data={data}
        titleNode={titleNode}
        loading={false}
      />
    </DefaultTableStyle>
  );
}

export function SubTable({ columns, data, titleNode, loading }) {
  return (
    <DefaultSubTableStyle>
      <UnstyledResizeableTable
        columns={columns}
        data={data}
        titleNode={titleNode}
        loading={loading}
      />
    </DefaultSubTableStyle>
  );
}

interface fetchDataArgs {
  pageSize: number;
  pageIndex: number;
}

interface Table<T extends ObjectType> {
  columns: Column<T>[];
  data: T[];
  titleNode?: React.ReactNode;
  fetchData: ({ pageSize, pageIndex }: fetchDataArgs) => Promise<void>;
  loading: boolean;
  limit: number;
  pageCount: number;
  renderRowSubComponent?: React.ComponentType;
  rowSubComponentProps?: ObjectType;
}

type UnstyledResizeablePaginatedTableT<T extends ObjectType> = (
  props: Table<T>
) => React.ReactElement;

// eslint-disable-next-line
type ObjectType = {};

export function UnstyledResizeablePaginatedTable<T extends ObjectType>({
  columns,
  data,
  titleNode,
  fetchData,
  loading,
  limit,
  pageCount: controlledPageCount,
  renderRowSubComponent,
  rowSubComponentProps,
}: Table<T>) {
  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 30,
      width: 115,
      maxWidth: 400,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    visibleColumns,
    // Get the state from the instance
    state: { pageIndex, pageSize },
  } = useTable<T>(
    {
      columns,
      data,
      defaultColumn,
      initialState: { pageIndex: 0, pageSize: limit },
      manualPagination: true,
      pageCount: controlledPageCount,
    },
    useBlockLayout,
    useExpanded,
    useResizeColumns,
    usePagination
  );

  // Listen for changes in pagination and use the state to fetch our new data
  React.useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  return (
    <>
      {titleNode}
      <div {...getTableProps()} className="table">
        <div className="thead">
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} className="tr">
              {headerGroup.headers.map((column) => (
                <div {...column.getHeaderProps()} className="th">
                  {column.render('Header')}
                  {/* Use column.getResizerProps to hook up the events correctly */}
                  <div
                    {...column.getResizerProps()}
                    className={`resizer ${
                      column.isResizing ? 'isResizing' : ''
                    }`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps()} className="tbody">
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <React.Fragment key={row.id || i}>
                <div {...row.getRowProps()} className="tr">
                  {row.cells.map((cell) => {
                    return (
                      <div {...cell.getCellProps()} className="td">
                        {cell.render('Cell')}
                      </div>
                    );
                  })}
                </div>
                {row.isExpanded ? (
                  renderRowSubComponent ? (
                    <div className="tr">
                      <div className="subTable">
                        {React.createElement(renderRowSubComponent, {
                          // eslint-disable-next-line
                          // @ts-ignore
                          row,
                          ...rowSubComponentProps,
                        })}
                      </div>
                    </div>
                  ) : (
                    <tr>
                      <td colSpan={visibleColumns.length}>
                        You're not supposed to be able to expand this
                      </td>
                    </tr>
                  )
                ) : null}
              </React.Fragment>
            );
          })}
          {!data.length && !loading && (
            <div className="td noData">No data available</div>
          )}
          <div className={`tr tableFooter ${loading ? 'loadingMask' : ''}`}>
            <div className="loadingIcon">
              {loading && <BusyIndicator width={42} />}
            </div>
            <div className="pagination">
              <IconButton
                className="button"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                <Icon size={1.1} path={mdiChevronDoubleLeft} />
              </IconButton>
              <IconButton
                className="button"
                onClick={previousPage}
                disabled={!canPreviousPage}
              >
                <Icon size={1.1} path={mdiChevronLeft} />
              </IconButton>
              <div className="pageCount">{pageIndex + 1}</div>
              <IconButton
                className="button"
                onClick={nextPage}
                disabled={!canNextPage}
              >
                <Icon size={1.1} path={mdiChevronRight} />
              </IconButton>
              <IconButton
                className="button"
                onClick={() => gotoPage(controlledPageCount - 1)}
                disabled={!canNextPage}
              >
                <Icon size={1.1} path={mdiChevronDoubleRight} />
              </IconButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * A resizeable table that supports pagination using react-table
 * @param columns The memoized headers of the table in the correct shape
 * @param data The data of shape [{accessor1:"value1"...accessorN:"valueN"}] (hook set by fetchData)
 * @param titleNode (Optional) Node displayed above the table
 * @param fetchData Callback to retrieve data from the API
 * @param limit The amount of results to retrieve
 *
 * EXAMPLE:
 * // Some data in the correct format
 * const serverData = makeData(10000)
 *
 * // We'll start our table without any data
  const [data, setData] = React.useState([])
  const [pageCount, setPageCount] = React.useState(0)
  const fetchIdRef = React.useRef(0)

  const fetchData = React.useCallback(({ pageSize, pageIndex }) => {
    // This will get called when the table needs new data
    // You could fetch your data from literally anywhere,
    // even a server. But for this example, we'll just fake it.

    // Give this fetch an ID
    const fetchId = ++fetchIdRef.current

    // Set the loading state
    setLoading(true)

    // We'll even set a delay to simulate a server here
    setTimeout(() => {
      // Only update the data if this is the latest fetch
      if (fetchId === fetchIdRef.current) {
        const startRow = pageSize * pageIndex
        const endRow = startRow + pageSize
        setData(serverData.slice(startRow, endRow))

        // Your server could send back total page count.
        // For now we'll just fake it, too
        setPageCount(Math.ceil(serverData.length / pageSize))

        setLoading(false)
      }
    }, 1000)
  }, [])
 */
export function ProvidedAPITable<T extends ObjectType>({
  columns,
  data,
  titleNode,
  fetchData,
  loading,
  limit,
  pageCount,
  renderRowSubComponent,
  rowSubComponentProps,
}: Table<T>) {
  return (
    <DefaultTableStyle>
      <UnstyledResizeablePaginatedTable
        columns={columns}
        data={data}
        titleNode={titleNode}
        fetchData={fetchData}
        loading={loading}
        limit={limit}
        pageCount={pageCount}
        renderRowSubComponent={renderRowSubComponent}
        rowSubComponentProps={rowSubComponentProps}
      />
    </DefaultTableStyle>
  );
}

export async function inflateData<T extends ObjectType, A extends ObjectType>(
  data: Array<T>,
  keys: ReadonlyArray<keyof T>
): Promise<Array<TypeSpread<T, A>>> {
  //! This is ugly, but I'm not sure if there is a way around it
  const inflated = data as unknown as Array<TypeSpread<T, A>>;

  for (const key of keys) {
    // Create a map from URL -> [Index1...IndexN]
    const dict: Record<string, number[]> = {};

    for (let index = 0; index < data.length; ++index) {
      const elem = data[index];
      const indexer = elem[key];

      if (typeof indexer === 'string') {
        Array.isArray(dict[indexer])
          ? dict[indexer].push(index)
          : (dict[indexer] = [index]);
      } else {
        // throw
      }
    }

    // Some URLs may 403, so catch these
    const promises = Object.keys(dict).map((url) =>
      api.get<A & { url: string }>(url)
    );

    // Grab each URL response
    const responses = await Promise.all(promises.map((p) => p.catch((e) => e)));
    const validResponses = responses.filter(
      (result) => !(result instanceof Error)
    );

    for (const r of validResponses) {
      for (const index of dict[r.data.url]) {
        //! I know this is crap, but I don't think there's a way around this with the current inflateData API we have.
        // eslint-disable-next-line
        // @ts-ignore
        inflated[index][key] = r.data;
      }
    }
  }

  return inflated;
}

interface APIResponse<T extends ObjectType> {
  count: number;
  next: string;
  previous: string;
  results: T[];
}

interface APITableProps<T extends unknown, A extends unknown> {
  columns: Column<TypeSpread<T, A>>[];
  titleNode?: React.ReactNode;
  url: string;
  params?: { [param: string]: string | string[] };
  limit: number;
  drillDown?: Array<keyof T>;
  renderRowSubComponent?: React.ComponentType<A>;
  rowSubComponentProps?: ObjectType;
}

/**
 * A table that supports pagination using react-table
 * The fetchData callback is automatically generated from the passes params
 * @param columns The memoized headers of the table in the correct shape
 * @param titleNode (Optional) Node displayed above the table
 * @param url Base api url to retrieve data from
 * @param params (Optional) The memoized query params DO NOT INCLUDE offset OR limit
 * @param limit The amount of results to retrieve
 * @param drillDown (Optional) The memoized array of props to drill down the API
 * @param renderRowSubComponent (Optional) The sub component/table for expanded rows
 * @param renderRowSubProps (Optional) The sub component/table props
 */
export function APITable<D extends unknown, A extends unknown>({
  columns,
  titleNode,
  url,
  params,
  limit,
  drillDown,
  renderRowSubComponent,
  rowSubComponentProps,
}: APITableProps<D, A>) {
  type Augmented = TypeSpread<D, A>;

  const [data, setData] = React.useState<Augmented[]>([]);
  const [pageCount, setPageCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(
    async ({ pageSize, pageIndex }): Promise<void> => {
      // Give this fetch an ID
      const fetchId = ++fetchIdRef.current;
      setLoading(true);

      try {
        const response = await api.get<APIResponse<D>>(url, {
          params: {
            limit: limit,
            offset: pageSize * pageIndex,
            ...params,
          },
        });

        const data = response.data.results;
        let inflatedData: Augmented[] = [];

        // Set JSON, making sure the accessors set in the columns match the JSON fields
        if (drillDown) {
          inflatedData = await inflateData<D, A>(data, drillDown);
          // Only update the data if this is the latest fetch
          if (fetchId === fetchIdRef.current) {
            setData(inflatedData);
            setLoading(false);
            setPageCount(Math.ceil(response.data.count / pageSize));
          }
        } else {
          // Only update the data if this is the latest fetch
          if (fetchId === fetchIdRef.current) {
            setData(data as unknown as Array<TypeSpread<D, A>>);
            setLoading(false);
            setPageCount(Math.ceil(response.data.count / pageSize));
          }
        }
      } catch (e) {
        // Pass
      }
    },
    [limit, drillDown, url, params]
  );

  const TypedTable: UnstyledResizeablePaginatedTableT<TypeSpread<D, A>> =
    UnstyledResizeablePaginatedTable;

  return (
    <DefaultTableStyle>
      <TypedTable
        columns={columns}
        data={data}
        titleNode={titleNode}
        fetchData={fetchData}
        loading={loading}
        limit={limit}
        pageCount={pageCount}
        renderRowSubComponent={renderRowSubComponent}
        rowSubComponentProps={rowSubComponentProps}
      />
    </DefaultTableStyle>
  );
}
